import { LogLevel } from '../diagnostics/log-level';
import { Logging } from '../diagnostics/logging';

/**
 * Language object which has a corresponding culture information of locale, name, and localized name.
 */
export interface Language {
    /**
     * Language locale identifier.
     */
    id: string;

    /**
     * Language English name.
     */
    name: string;

    /**
     * Language localized name.
     */
    localizedName: string;
}

export class LanguageInventory {
    public neutralCultures: Language[] = [
        { id: 'en-US', name: 'English', localizedName: 'English' },
        { id: 'cs-CZ', name: 'Czech', localizedName: 'Čeština' },
        { id: 'de-DE', name: 'German', localizedName: 'Deutsch' },
        { id: 'es-ES', name: 'Spanish', localizedName: 'Español' },
        { id: 'fr-FR', name: 'French', localizedName: 'Français' },
        { id: 'hu-HU', name: 'Hungarian', localizedName: 'Magyar' },
        { id: 'it-IT', name: 'Italian', localizedName: 'Italiano' },
        { id: 'ja-JP', name: 'Japanese', localizedName: '日本語' },
        { id: 'ko-KR', name: 'Korean', localizedName: '한국어' },
        { id: 'nl-NL', name: 'Dutch', localizedName: 'Nederlands' },
        { id: 'pl-PL', name: 'Polish', localizedName: 'Polski' },
        { id: 'pt-BR', name: 'Portuguese (Brasil)', localizedName: 'Português (Brasil)' },
        { id: 'pt-PT', name: 'Portuguese (Portugal)', localizedName: 'Português (Portugal)' },
        { id: 'ru-RU', name: 'Russian', localizedName: 'Русский' },
        { id: 'sv-SE', name: 'Swedish', localizedName: 'Svenska' },
        { id: 'tr-TR', name: 'Turkish', localizedName: 'Türkçe' },
        { id: 'zh-CN', name: 'Chinese (Simplified)', localizedName: '中文(简体)' },
        { id: 'zh-TW', name: 'Chinese (Traditional)', localizedName: '中文(繁體)' }
    ];
    public get regionalCultures(): Language[] {
        return this.getRegionalCulturesMap();
    }

    private getRegionalCulturesMap() {
        const otherCultures = [
            this.makeGermanCulture('be', 'Belgium', 'Belgien'),
            this.makeGermanCulture('de', 'Germany', 'Deutschland'),
            this.makeGermanCulture('li', 'Liechtenstein', 'Liechtenstein'),
            this.makeGermanCulture('lu', 'Luxembourg', 'Luxemburg'),
            this.makeGermanCulture('ch', 'Switzerland', 'Schweiz'),
            this.makeGermanCulture('at', 'Austria', 'Österreich'),
            this.makeEnglishCulture('as', 'American Samoa'),
            this.makeEnglishCulture('ai', 'Anguilla'),
            this.makeEnglishCulture('ag', 'Antigua and Barbuda'),
            this.makeEnglishCulture('au', 'Australia'),
            this.makeEnglishCulture('bs', 'Bahamas'),
            this.makeEnglishCulture('bb', 'Barbados'),
            this.makeEnglishCulture('be', 'Belgium'),
            this.makeEnglishCulture('bz', 'Belize'),
            this.makeEnglishCulture('bm', 'Bermuda'),
            this.makeEnglishCulture('bw', 'Botswana'),
            this.makeEnglishCulture('io', 'British Indian Ocean Territory'),
            this.makeEnglishCulture('vg', 'British Virgin Islands'),
            this.makeEnglishCulture('cm', 'Cameroon'),
            this.makeEnglishCulture('ca', 'Canada'),
            this.makeEnglishCulture('029', 'Caribbean'),
            this.makeEnglishCulture('ky', 'Cayman Islands'),
            this.makeEnglishCulture('cx', 'Christmas Island'),
            this.makeEnglishCulture('cc', 'Cocos (Keeling) Islands'),
            this.makeEnglishCulture('ck', 'Cook Islands'),
            this.makeEnglishCulture('dm', 'Dominica'),
            this.makeEnglishCulture('er', 'Eritrea'),
            this.makeEnglishCulture('150', 'Europe'),
            this.makeEnglishCulture('fk', 'Falkland Islands'),
            this.makeEnglishCulture('fj', 'Fiji'),
            this.makeEnglishCulture('gm', 'Gambia'),
            this.makeEnglishCulture('gh', 'Ghana'),
            this.makeEnglishCulture('gi', 'Gibraltar'),
            this.makeEnglishCulture('gd', 'Grenada'),
            this.makeEnglishCulture('gu', 'Guam'),
            this.makeEnglishCulture('gg', 'Guernsey'),
            this.makeEnglishCulture('gy', 'Guyana'),
            this.makeEnglishCulture('hk', 'Hong Kong SAR'),
            this.makeEnglishCulture('in', 'India'),
            this.makeEnglishCulture('ie', 'Ireland'),
            this.makeEnglishCulture('im', 'Isle of Man'),
            this.makeEnglishCulture('jm', 'Jamaica'),
            this.makeEnglishCulture('je', 'Jersey'),
            this.makeEnglishCulture('ke', 'Kenya'),
            this.makeEnglishCulture('ki', 'Kiribati'),
            this.makeEnglishCulture('ls', 'Lesotho'),
            this.makeEnglishCulture('lr', 'Liberia'),
            this.makeEnglishCulture('mo', 'Macao SAR'),
            this.makeEnglishCulture('mg', 'Madagascar'),
            this.makeEnglishCulture('mw', 'Malawi'),
            this.makeEnglishCulture('my', 'Malaysia'),
            this.makeEnglishCulture('mt', 'Malta'),
            this.makeEnglishCulture('mh', 'Marshall Islands'),
            this.makeEnglishCulture('mu', 'Mauritius'),
            this.makeEnglishCulture('fm', 'Micronesia'),
            this.makeEnglishCulture('ms', 'Montserrat'),
            this.makeEnglishCulture('na', 'Namibia'),
            this.makeEnglishCulture('nr', 'Nauru'),
            this.makeEnglishCulture('nz', 'New Zealand'),
            this.makeEnglishCulture('ng', 'Nigeria'),
            this.makeEnglishCulture('nu', 'Niue'),
            this.makeEnglishCulture('nf', 'Norfolk Island'),
            this.makeEnglishCulture('mp', 'Northern Mariana Islands'),
            this.makeEnglishCulture('pk', 'Pakistan'),
            this.makeEnglishCulture('pw', 'Palau'),
            this.makeEnglishCulture('pg', 'Papua New Guinea'),
            this.makeEnglishCulture('ph', 'Philippines'),
            this.makeEnglishCulture('pn', 'Pitcairn Islands'),
            this.makeEnglishCulture('pr', 'Puerto Rico'),
            this.makeEnglishCulture('rw', 'Rwanda'),
            this.makeEnglishCulture('kn', 'Saint Kitts and Nevis'),
            this.makeEnglishCulture('lc', 'Saint Lucia'),
            this.makeEnglishCulture('vc', 'Saint Vincent and the Grenadines'),
            this.makeEnglishCulture('ws', 'Samoa'),
            this.makeEnglishCulture('sc', 'Seychelles'),
            this.makeEnglishCulture('sl', 'Sierra Leone'),
            this.makeEnglishCulture('sg', 'Singapore'),
            this.makeEnglishCulture('sx', 'Sint Maarten'),
            this.makeEnglishCulture('sb', 'Solomon Islands'),
            this.makeEnglishCulture('za', 'South Africa'),
            this.makeEnglishCulture('ss', 'South Sudan'),
            this.makeEnglishCulture('sh', 'St Helena, Ascension, Tristan da Cunha'),
            this.makeEnglishCulture('sd', 'Sudan'),
            this.makeEnglishCulture('sz', 'Swaziland'),
            this.makeEnglishCulture('tz', 'Tanzania'),
            this.makeEnglishCulture('tk', 'Tokelau'),
            this.makeEnglishCulture('to', 'Tonga'),
            this.makeEnglishCulture('tt', 'Trinidad and Tobago'),
            this.makeEnglishCulture('tc', 'Turks and Caicos Islands'),
            this.makeEnglishCulture('tv', 'Tuvalu'),
            this.makeEnglishCulture('um', 'U.S. Outlying Islands'),
            this.makeEnglishCulture('vi', 'U.S. Virgin Islands'),
            this.makeEnglishCulture('ug', 'Uganda'),
            this.makeEnglishCulture('gb', 'United Kingdom'),
            this.makeEnglishCulture('us', 'United States'),
            this.makeEnglishCulture('vu', 'Vanuatu'),
            this.makeEnglishCulture('001', 'World'),
            this.makeEnglishCulture('zm', 'Zambia'),
            this.makeEnglishCulture('zw', 'Zimbabwe'),
            this.makeSpanishCulture('ar', 'Argentina', 'Argentina'),
            this.makeSpanishCulture('bo', 'Bolivia', 'Bolivia'),
            this.makeSpanishCulture('cl', 'Chile', 'Chile'),
            this.makeSpanishCulture('co', 'Colombia', 'Colombia'),
            this.makeSpanishCulture('cr', 'Costa Rica', 'Costa Rica'),
            this.makeSpanishCulture('cu', 'Cuba', 'Cuba'),
            this.makeSpanishCulture('ec', 'Ecuador', 'Ecuador'),
            this.makeSpanishCulture('sv', 'El Salvador', 'El Salvador'),
            this.makeSpanishCulture('es', 'Spain, International Sort', 'España, alfabetización internacional'),
            this.makeSpanishCulture('us', 'United States', 'Estados Unidos'),
            this.makeSpanishCulture('ph', 'Philippines', 'Filipinas'),
            this.makeSpanishCulture('gt', 'Guatemala', 'Guatemala'),
            this.makeSpanishCulture('gq', 'Equatorial Guinea', 'Guinea Ecuatorial'),
            this.makeSpanishCulture('hn', 'Honduras', 'Honduras'),
            this.makeSpanishCulture('419', 'Latin America', 'Latinoamérica'),
            this.makeSpanishCulture('mx', 'Mexico', 'México'),
            this.makeSpanishCulture('ni', 'Nicaragua', 'Nicaragua'),
            this.makeSpanishCulture('pa', 'Panama', 'Panamá'),
            this.makeSpanishCulture('py', 'Paraguay', 'Paraguay'),
            this.makeSpanishCulture('pe', 'Peru', 'Perú'),
            this.makeSpanishCulture('pr', 'Puerto Rico', 'Puerto Rico'),
            this.makeSpanishCulture('do', 'Dominican Republic', 'República Dominicana'),
            this.makeSpanishCulture('uy', 'Uruguay', 'Uruguay'),
            this.makeSpanishCulture('ve', 'Venezuela', 'Venezuela'),
            this.makeFrenchCulture('dz', 'Algeria', 'Algérie'),
            this.makeFrenchCulture('be', 'Belgium', 'Belgique'),
            this.makeFrenchCulture('bf', 'Burkina Faso', 'Burkina Faso'),
            this.makeFrenchCulture('bi', 'Burundi', 'Burundi'),
            this.makeFrenchCulture('bj', 'Benin', 'Bénin'),
            this.makeFrenchCulture('cm', 'Cameroon', 'Cameroun'),
            this.makeFrenchCulture('ca', 'Canada', 'Canada'),
            this.makeFrenchCulture('km', 'Comoros', 'Comores'),
            this.makeFrenchCulture('cg', 'Congo', 'Congo'),
            this.makeFrenchCulture('cd', 'French Congo (DRC)', 'Congo, République démocratique du'),
            this.makeFrenchCulture('ci', 'Côte d’Ivoire', 'Côte d’Ivoire'),
            this.makeFrenchCulture('dj', 'Djibouti', 'Djibouti'),
            this.makeFrenchCulture('fr', 'France', 'France'),
            this.makeFrenchCulture('ga', 'Gabon', 'Gabon'),
            this.makeFrenchCulture('gp', 'Guadeloupe', 'Guadeloupe'),
            this.makeFrenchCulture('gq', 'Equatorial Guinea', 'Guinée équatoriale'),
            this.makeFrenchCulture('gn', 'Guinea', 'Guinée'),
            this.makeFrenchCulture('gf', 'French Guiana', 'Guyane française'),
            this.makeFrenchCulture('ht', 'Haiti', 'Haïti'),
            this.makeFrenchCulture('re', 'Réunion', 'La Réunion'),
            this.makeFrenchCulture('lu', 'Luxembourg', 'Luxembourg'),
            this.makeFrenchCulture('mg', 'Madagascar', 'Madagascar'),
            this.makeFrenchCulture('ml', 'Mali', 'Mali'),
            this.makeFrenchCulture('ma', 'Morocco', 'Maroc'),
            this.makeFrenchCulture('mq', 'Martinique', 'Martinique'),
            this.makeFrenchCulture('mu', 'Mauritius', 'Maurice'),
            this.makeFrenchCulture('mr', 'Mauritania', 'Mauritanie'),
            this.makeFrenchCulture('yt', 'Mayotte', 'Mayotte'),
            this.makeFrenchCulture('mc', 'Monaco', 'Monaco'),
            this.makeFrenchCulture('ne', 'Niger', 'Niger'),
            this.makeFrenchCulture('nc', 'New Caledonia', 'Nouvelle-Calédonie'),
            this.makeFrenchCulture('pf', 'French Polynesia', 'Polynésie française'),
            this.makeFrenchCulture('rw', 'Rwanda', 'Rwanda'),
            this.makeFrenchCulture('cf', 'Central African Republic', 'République centrafricaine'),
            this.makeFrenchCulture('bl', 'Saint Barthélemy', 'Saint-Barthélemy'),
            this.makeFrenchCulture('mf', 'Saint Martin', 'Saint-Martin'),
            this.makeFrenchCulture('pm', 'Saint Pierre and Miquelon', 'Saint-Pierre-et-Miquelon'),
            this.makeFrenchCulture('sc', 'Seychelles', 'Seychelles'),
            this.makeFrenchCulture('ch', 'Switzerland', 'Suisse'),
            this.makeFrenchCulture('sy', 'Syria', 'Syrie'),
            this.makeFrenchCulture('sn', 'Senegal', 'Sénégal'),
            this.makeFrenchCulture('td', 'Chad', 'Tchad'),
            this.makeFrenchCulture('tg', 'Togo', 'Togo'),
            this.makeFrenchCulture('tn', 'Tunisia', 'Tunisie'),
            this.makeFrenchCulture('vu', 'Vanuatu', 'Vanuatu'),
            this.makeFrenchCulture('wf', 'Wallis and Futuna', 'Wallis-et-Futuna'),
            ['it-IT', 'Italian (Italy)', 'Italiano (Italia)'],
            ['it-SM', 'Italian (San Marino)', 'Italiano (San Marino)'],
            ['it-CH', 'Italian (Switzerland)', 'Italiano (Svizzera)'],
            ['hu-HU', 'Hungarian (Hungary)', 'Magyar (Magyarország)'],
            ['nl-AW', 'Dutch (Aruba)', 'Nederlands (Aruba)'],
            ['nl-BE', 'Dutch (Belgium)', 'Nederlands (België)'],
            ['nl-BQ', 'Dutch (Bonaire, Sint Eustatius and Saba)', 'Nederlands (Bonaire, Sint Eustatius en Saba)'],
            ['nl-CW', 'Dutch (Curaçao)', 'Nederlands (Curaçao)'],
            ['nl-NL', 'Dutch (Netherlands)', 'Nederlands (Nederland)'],
            ['nl-SX', 'Dutch (Sint Maarten)', 'Nederlands (Sint-Maarten)'],
            ['nl-SR', 'Dutch (Suriname)', 'Nederlands (Suriname)'],
            ['pl-PL', 'Polish (Poland)', 'Polski (Polska)'],
            this.makePortugueseCulture('br', 'Brazil', 'Brasil'),
            this.makePortugueseCulture('pt', 'Portugal', 'Portugal'),
            ['sv-FI', 'Swedish (Finland)', 'Svenska (Finland)'],
            ['sv-SE', 'Swedish (Sweden)', 'Svenska (Sverige)'],
            ['sv-AX', 'Swedish (Åland Islands)', 'Svenska (Åland)'],
            ['tr-CY', 'Turkish (Cyprus)', 'Türkçe (Kıbrıs)'],
            ['tr-TR', 'Turkish (Turkey)', 'Türkçe (Türkiye)'],
            ['cs-CZ', 'Czech (Czechia)', 'Čeština (Česko)'],
            ['ru-BY', 'Russian (Belarus)', 'Русский (Беларусь)'],
            ['ru-KZ', 'Russian (Kazakhstan)', 'Русский (Казахстан)'],
            ['ru-KG', 'Russian (Kyrgyzstan)', 'Русский (Киргизия)'],
            ['ru-MD', 'Russian (Moldova)', 'Русский (Молдова)'],
            ['ru-RU', 'Russian (Russia)', 'Русский (Россия)'],
            ['ru-UA', 'Russian (Ukraine)', 'Русский (Украина)'],
            ['zh-CN', 'Chinese (Simplified, China)', '中文(中国)'],
            ['zh-TW', 'Chinese (Traditional, Taiwan)', '中文(台灣)'],
            ['zh-SG', 'Chinese (Simplified, Singapore)', '中文(新加坡)'],
            ['zh-MO', 'Chinese (Traditional, Macao SAR)', '中文(澳門特別行政區)'],
            ['zh-HK', 'Chinese (Traditional, Hong Kong SAR)', '中文(香港特別行政區)'],
            ['ja-JP', 'Japanese (Japan)', '日本語 (日本)'],
            ['ko-KP', 'Korean (North Korea)', '한국어 (조선민주주의인민공화국)'],
            ['ko-KR', 'Korean (Korea)', '한국어(대한민국)']
        ];

        const map: Language[] = [];
        otherCultures.forEach(item => {
            const lang: Language = {
                id: item[0],
                name: item[1],
                localizedName: item[2]
            };

            // Windows Admin Center uses browser's native Intl supports.
            // Microsoft Edge and Chrome return different locale ID with the following API,
            // here it checks only if it responds with supported one item.
            const support = Intl.DateTimeFormat.supportedLocalesOf(lang.id);
            if (support.length === 1) {
                map.push(lang);
            } else if (Intl.DateTimeFormat.supportedLocalesOf('ja-JP').length > 0) {
                // Test environment lack of standard language support like ja-JP.
                // If there is ja-JP but this reginal code is not supporte, sprits out warning message.
                // This doesn't happen on Microsoft Edge and Google Chrome.
                Logging.log({
                    level: LogLevel.Warning,
                    source: 'core/language',
                    message: 'Unsupporte regional language by Intl.DateTimeFormat.supportedLocalesOf("{0}")'.format(lang.id)
                });
            }
        });

        return map;
    }

    private makeEnglishCulture(code: string, englishSubCulture: string): string[] {
        return [`en-${code.toUpperCase()}`, `English (${englishSubCulture})`, `English (${englishSubCulture})`];
    }

    private makeGermanCulture(code: string, englishSubCulture: string, nativeSubCulture: string): string[] {
        return [`de-${code.toUpperCase()}`, `German (${englishSubCulture})`, `Deutsch (${nativeSubCulture})`];
    }

    private makeSpanishCulture(code: string, englishSubCulture: string, nativeSubCulture: string): string[] {
        return [`es-${code.toUpperCase()}`, `Spanish (${englishSubCulture})`, `Español (${nativeSubCulture})`];
    }

    private makeFrenchCulture(code: string, englishSubCulture: string, nativeSubCulture: string): string[] {
        return [`fr-${code.toUpperCase()}`, `French (${englishSubCulture})`, `Français (${nativeSubCulture})`];
    }

    private makePortugueseCulture(code: string, englishSubCulture: string, nativeSubCulture: string): string[] {
        return [`pt-${code.toUpperCase()}`, `Portuguese (${englishSubCulture})`, `Português (${nativeSubCulture})`];
    }
}
